import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { Chart } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { api } from '@/services/api';
import { alignByDay, computeRSMap, computeEMAMap, clampEmaPeriod } from '@/services/rsCalculator';
import type { RSRenderConfig } from '@/indicators/rsLine';
import type { OHLCVBar } from '@/types';

type ChartV9 = Chart & {
  createIndicator: (name: string, isStack: boolean, opts?: { id?: string }) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (o: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId: string) => void;
  getDataList: () => Array<{ timestamp: number; close: number }>;
};

const RS_INDICATOR_NAME = 'RS_LINE';
const RS_PANE_ID        = 'candle_pane';

// ── RS raw ratio map (unscaled, for new-high detection) ─────────────────────
function computeRSRawMap(
  stockBars: OHLCVBar[],
  benchmarkDayMap: Map<number, number>,
): Map<number, number> {
  const out = new Map<number, number>();
  for (const bar of stockBars) {
    const dayKey    = Math.floor(bar.time / 86400);
    const benchClose = benchmarkDayMap.get(dayKey);
    if (benchClose && benchClose > 0 && bar.close > 0) {
      out.set(bar.time, bar.close / benchClose);
    }
  }
  return out;
}

// ── Shorten benchmark name for label display ─────────────────────────────────
function shortLabel(name: string, symbol: string): string {
  if (name.length <= 8) return name;
  // Use symbol if it's shorter and meaningful
  const sym = symbol.replace(/\.[A-Z]\d+$/, ''); // strip ".N0000" etc.
  return sym.length <= 8 ? sym : name.slice(0, 8);
}

export function useRSLines(chartRef: RefObject<Chart | null>): void {
  const rsLineConfigs   = useChartStore((s) => s.rsLineConfigs);
  const symbol          = useChartStore((s) => s.symbol);
  const timeframe       = useChartStore((s) => s.timeframe);
  const priceAdjustments = useChartStore((s) => s.priceAdjustments);
  // Subscribe to chart instance so the effect re-fires when the chart is
  // created (e.g. after a hard refresh where rsLineConfigs is already hydrated
  // from localStorage but chartRef.current was null on first run).
  const chartInstance   = useChartStore((s) => s.chart);
  const indicatorPaneMap = useChartStore((s) => s.indicatorPaneMap);
  const setIndicatorPaneMap = useChartStore((s) => s.setIndicatorPaneMap);

  // Cache stock daily bars by symbol — avoids re-fetching when only RS config changes
  const stockDailyCacheRef     = useRef<Map<string, OHLCVBar[]>>(new Map());
  // Cache benchmark daily bars by benchmarkSymbol — benchmark data is independent of the
  // charted stock symbol so it is NOT cleared on symbol change. This also prevents configs
  // that share the same benchmarkSymbol from hitting the API twice.
  const benchmarkDailyCacheRef = useRef<Map<string, OHLCVBar[]>>(new Map());
  // Cache bars for custom stock-vs-stock comparison lines (keyed by stockSymbol).
  // Not cleared on charted-symbol change — the custom stock is independent.
  const comparisonStockCacheRef = useRef<Map<string, OHLCVBar[]>>(new Map());
  // Cache pre-computed RS ratings by symbol (from screener_daily via API).
  const rsRatingCacheRef = useRef<Map<string, number>>(new Map());
  const prevSymbolRef           = useRef<string | null>(null);
  const prevAdjustRef           = useRef<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.__rs_lines = [];
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current as ChartV9 | null;
    if (!chart) return;

    // Invalidate stock cache on symbol change — must run synchronously before the
    // async loadRSLines reads the cache. A separate useEffect([symbol]) fires too
    // late (after this effect body has already started executing).
    if (prevSymbolRef.current !== symbol) {
      stockDailyCacheRef.current.clear();
      rsRatingCacheRef.current.clear();
      prevSymbolRef.current = symbol;
    }

    // Invalidate ALL price-bar caches when the adjustment selection changes — every
    // cached series (stock, benchmark, comparison stock) was fetched under the
    // previous selection and is now stale. RS rating is unaffected (separate
    // backend endpoint), so it's deliberately not cleared here.
    const adjustKey = priceAdjustments.join(',');
    if (prevAdjustRef.current !== adjustKey) {
      stockDailyCacheRef.current.clear();
      benchmarkDailyCacheRef.current.clear();
      comparisonStockCacheRef.current.clear();
      prevAdjustRef.current = adjustKey;
    }

    const visibleConfigs = rsLineConfigs.filter((c) => c.visible);

    // ── Remove indicator if no visible lines ─────────────────────────────
    if (visibleConfigs.length === 0) {
      window.__rs_lines = [];
      if (indicatorPaneMap.has(RS_INDICATOR_NAME)) {
        try { chart.removeIndicator(RS_PANE_ID, RS_INDICATOR_NAME); } catch { /* ignore */ }
        const next = new Map(indicatorPaneMap);
        next.delete(RS_INDICATOR_NAME);
        setIndicatorPaneMap(next);
      }
      // Force immediate canvas repaint so the RS lines disappear right away
      const chartResizable = chart as unknown as { resize?: () => void };
      chartResizable.resize?.();
      return;
    }

    let cancelled = false;

    async function loadRSLines() {
      const chart = chartRef.current as ChartV9 | null;
      if (!chart) return;

      // ── Fetch stock daily bars (always API; cached per symbol) ───────
      // Do NOT use chart.getDataList() — it still holds the previous symbol's
      // bars when this effect fires before ChartContainer's symbol-change effect.
      let stockDailyBars: OHLCVBar[];
      const cached = stockDailyCacheRef.current.get(symbol);

      if (cached) {
        stockDailyBars = cached;
      } else {
        try {
          const res = await api.ohlcv(symbol, '1D', 2000, undefined, undefined, priceAdjustments);
          if (cancelled) return;
          stockDailyBars = res.data;
          stockDailyCacheRef.current.set(symbol, stockDailyBars);
        } catch {
          return;
        }
      }

      if (cancelled) return;

      // ── Fetch benchmark data + compute RS per config ─────────────────
      // Each benchmarkSymbol is fetched at most once per hook lifetime — the
      // benchmark cache is keyed by symbol and NOT invalidated on stock-symbol
      // changes.  This prevents two configs that share the same benchmarkSymbol
      // from receiving silently different (or accidentally same) data objects.
      const results = await Promise.allSettled(
        rsLineConfigs.map(async (cfg) => {
          // Benchmark bars
          const cachedBench = benchmarkDailyCacheRef.current.get(cfg.benchmarkSymbol);
          let benchBars: OHLCVBar[];
          if (cachedBench) {
            benchBars = cachedBench;
          } else {
            const benchRes = await api.ohlcv(cfg.benchmarkSymbol, '1D', 2000, undefined, undefined, priceAdjustments);
            if (!cancelled) {
              benchmarkDailyCacheRef.current.set(cfg.benchmarkSymbol, benchRes.data);
            }
            benchBars = benchRes.data;
          }

          // Custom stock bars (stock-vs-stock comparison)
          let stockBarsForLine: OHLCVBar[] = stockDailyBars!;
          if (cfg.stockSymbol) {
            const cachedComp = comparisonStockCacheRef.current.get(cfg.stockSymbol);
            if (cachedComp) {
              stockBarsForLine = cachedComp;
            } else {
              const compRes = await api.ohlcv(cfg.stockSymbol, '1D', 2000, undefined, undefined, priceAdjustments);
              if (!cancelled) {
                comparisonStockCacheRef.current.set(cfg.stockSymbol, compRes.data);
              }
              stockBarsForLine = compRes.data;
            }
          }

          return { cfg, benchBars, stockBarsForLine };
        })
      );

      if (cancelled) return;

      const renderConfigs: RSRenderConfig[] = [];

      // Collect distinct symbols needing a rating lookup (not in cache)
      const ratingSymbols = new Set<string>();
      for (const result of results) {
        if (result.status === 'rejected') continue;
        const ratingKey = result.value.cfg.stockSymbol ?? symbol;
        if (!rsRatingCacheRef.current.has(ratingKey)) {
          ratingSymbols.add(ratingKey);
        }
      }

      // Fetch all missing ratings in parallel
      await Promise.allSettled(
        Array.from(ratingSymbols).map(async (sym) => {
          try {
            const res = await api.screenerRating(sym);
            if (!cancelled && res.rs_rating != null) {
              rsRatingCacheRef.current.set(sym, res.rs_rating);
            }
          } catch { /* ignore — falls back to 0 */ }
        })
      );

      if (cancelled) return;

      for (const result of results) {
        if (result.status === 'rejected') continue;
        const { cfg, benchBars, stockBarsForLine } = result.value;

        const benchDayMap = alignByDay(benchBars);
        const rsMap       = computeRSMap(stockBarsForLine, benchDayMap, 1.0);
        const rsRawMap    = computeRSRawMap(stockBarsForLine, benchDayMap);
        const emaPeriod   = clampEmaPeriod(cfg.emaPeriod);
        const emaMap      = cfg.emaEnabled ? computeEMAMap(rsMap, emaPeriod) : null;
        const ratingKey   = cfg.stockSymbol ?? symbol;
        const rsRating    = rsRatingCacheRef.current.get(ratingKey) ?? 0;
        const rawEntries  = Array.from(rsRawMap.entries()).sort((a, b) => a[0] - b[0]);

        // Label: for stock-vs-stock show "STOCK/BENCH", otherwise benchmark short name
        const lineLabel = cfg.stockSymbol
          ? `${cfg.stockSymbol}/${shortLabel(cfg.benchmarkName, cfg.benchmarkSymbol)}`
          : shortLabel(cfg.benchmarkName, cfg.benchmarkSymbol);

        renderConfigs.push({
          id:        cfg.id,
          color:     cfg.color,
          visible:   cfg.visible,
          label:     lineLabel,
          rsMap,
          rsRawMap,
          rsRating,
          rawEntries,
          offsetPx: cfg.offsetPx ?? 0,
          emaMap,
          emaPeriod,
        });
      }

      // ── Update window global ─────────────────────────────────────────
      window.__rs_lines = renderConfigs;

      // ── Ensure indicator is on chart ─────────────────────────────────
      const currentPaneMap = useChartStore.getState().indicatorPaneMap;
      if (!currentPaneMap.has(RS_INDICATOR_NAME)) {
        try {
          chart.createIndicator(RS_INDICATOR_NAME, true, { id: RS_PANE_ID });
          const next = new Map(currentPaneMap);
          next.set(RS_INDICATOR_NAME, RS_PANE_ID);
          setIndicatorPaneMap(next);
        } catch { /* ignore */ }
      }

      // ── Force canvas repaint ──────────────────────────────────────────
      // resize() is a no-op when chart dimensions haven't changed (symbol switch,
      // visibility toggle). overrideIndicator() always triggers a full calc+draw
      // cycle. We still nest in RAF so that if createIndicator's internal .then()
      // scheduled a frame, ours runs in the NEXT frame after it completes.
      requestAnimationFrame(() => {
        const liveChart = chartRef.current as ChartV9 | null;
        if (liveChart) {
          try {
            liveChart.overrideIndicator({ name: RS_INDICATOR_NAME, styles: {} }, RS_PANE_ID);
          } catch { /* ignore */ }
          const chartResizable = liveChart as unknown as { resize?: () => void };
          chartResizable.resize?.();
        }
      });
    }

    loadRSLines();

    return () => {
      cancelled = true;
    };
  // Intentionally exclude indicatorPaneMap and setIndicatorPaneMap from deps —
  // they are read via getState() inside the async body to avoid stale closures.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsLineConfigs, symbol, timeframe, chartInstance, priceAdjustments]);

}
